import React, { useState, useEffect, useCallback, useContext } from "react";
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import {
  User,
  Phone,
  Heart,
  Calendar,
  MapPin,
  Droplet,
  FileText,
  ClipboardList,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import ReasonDropdown from "../Comman/ReasonDropdown";
import { set } from "lodash";
import { AuthContext } from "../../../context/AuthContext";
import AddCategoryItemModal from "../Comman/AddCategoryItemModal";
import SuperPatientProfile from "./SuperPatientProfile";

const DoctorUsersList = () => {

  const {
    timeBlocks,
    reasonOptions
  } = useContext(AuthContext);

  const navigate = useNavigate();

  const [isSuperPatientProfileOpen, setIsSuperPatientProfileOpen] = useState(false);
  const [APIcallLoading, setAPIcallLoading] = useState(false);
  const [profileId, setProfileId] = useState(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("allergiesName");


  // Core state
  const [allPatients, setAllPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // UI state
  const [expandedTiles, setExpandedTiles] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [checkupInfo, setCheckupInfo] = useState("");
  const [age, setAge] = useState(0);

  // Appointment states
  const [appointmentDate, setAppointmentDate] = useState("");
  const [timeBlock, setTimeBlock] = useState("");
  const [reason, setReason] = useState("");
  const [otherReason, setOtherReason] = useState("");

  // NEW: appointment submit loading
  const [creatingAppointment, setCreatingAppointment] = useState(false);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, genderFilter, statusFilter, itemsPerPage]);

  // Fetch patients
  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        role: "patient",
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(genderFilter && { gender: genderFilter }),
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await API.get(`/api/doctor/get-all-users?${params}`);

      if (response.data.success) {
        setAllPatients(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalRecords(response.data.pagination.totalRecords);
      } else {
        setError("Failed to fetch patients");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching patients");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearchTerm,
    genderFilter,
    statusFilter,
  ]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Helper functions
  const calculateAge = useCallback((dateOfBirth) => {
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const renderField = (value, fallback = "Not provided") => {
    if (Array.isArray(value)) return value.join(", ") || fallback;
    return value || fallback;
  };

  // UI handlers
  const toggleTile = useCallback((id) => {
    setExpandedTiles((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const clearFilters = () => {
    setSearchTerm("");
    setGenderFilter("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  // Modal handlers
  const openModal = useCallback(
    (user) => {
      setSelectedUser(user);
      setAge(
        user.userDetails?.dateOfBirth
          ? calculateAge(user.userDetails.dateOfBirth)
          : "N/A"
      );
      setIsModalOpen(true);
    },
    [calculateAge]
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setCheckupInfo("");
    setAge(0);
    setSelectedUser(null);
  };

  const openAppointmentModal = useCallback(
    (user) => {
      setSelectedUser(user);
      setAge(
        user.userDetails?.dateOfBirth
          ? calculateAge(user.userDetails.dateOfBirth)
          : "N/A"
      );
      setIsAppointmentModalOpen(true);
    },
    [calculateAge]
  );

  const closeAppointmentModal = () => {
    setIsAppointmentModalOpen(false);
    setAppointmentDate("");
    setTimeBlock("");
    setReason("");
    setOtherReason("");
    setSelectedUser(null);
  };

  // Form submissions
  const handleCreateOPD = async (user) => {
    try {
      console.log("Creating OPD for user:", user);

      // Validate required data exists
      if (!user._id) {
        toast.error("User ID is missing");
        return;
      }

      if (!user.userDetails?._id) {
        toast.error("User details are missing");
        return;
      }

      if (!user.medicalDetails?._id) {
        toast.error("Medical details are missing for this user");
        return;
      }

      const response = await API.post(`/api/doctor/existing-user-opd`, {
        age: typeof age === "number" ? age : null,
        checkupInfo,
        user: user._id,
        medicalDetails: user.medicalDetails._id,
        userDetails: user.userDetails._id,
      });

      toast.success("OPD Created Successfully!");
      navigate(`/dashboard/edit-opd/${response.data}`);
    } catch (error) {
      console.error("Error creating OPD:", error);
      toast.error(error.response?.data?.message || "Failed to create OPD.");
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();

    // Use dedicated appointment loading state
    setCreatingAppointment(true);

    // Basic validations -> ensure we clear creating state before returning
    if (!appointmentDate || !timeBlock || !reason) {
      setCreatingAppointment(false);
      toast.error("Please fill in all required fields.");
      return;
    }

    if (reason === "Other" && !otherReason.trim()) {
      setCreatingAppointment(false);
      toast.error("Please specify the reason for the visit.");
      return;
    }

    let myappointmentreason;
    if (reason === "Others") {
      myappointmentreason = otherReason.trim();
    } else {
      myappointmentreason = reason;
    }

    console.log("otherReason", otherReason);
    console.log("reason", reason);
    console.log("myappointmentreason", myappointmentreason);

    try {
      await API.post(`/api/users/appointment`, {
        userId: selectedUser._id,
        date: appointmentDate,
        timeBlock,
        status: "Scheduled",
        reason: myappointmentreason,
      });

      toast.success("Appointment created successfully!");

      closeAppointmentModal();
    } catch (error) {
      console.error(" Error creating appointment:", error);
      toast.error(
        error.response?.data?.message || "Failed to create appointment."
      );
    } finally {
      // Always reset creating flag
      setCreatingAppointment(false);
    }
  };

  if (loading && allPatients.length === 0) {
    return <Loading />;
  }

  const today = new Date();
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 14);

  return (
    <div className="mx-auto p-6 space-y-6">
      {/* Header */}

      <div className="text-center bg-white rounded-2xl shadow-sm p-6 border">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          All Patients
        </h1>
        <p className="text-gray-600 text-lg">Manage patient records and create appointments</p>
      </div>

      {/* Search & Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or mobile..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Items per page */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Show:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              {[5, 10, 15, 20, 25, 50, 100, 200].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Stats */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {totalRecords}
              </div>
              <div className="text-sm text-gray-600">Total Patients</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {currentPage}
              </div>
              <div className="text-sm text-gray-600">Current Page</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {totalPages}
              </div>
              <div className="text-sm text-gray-600">Total Pages</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {allPatients.length}
              </div>
              <div className="text-sm text-gray-600">Showing</div>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-600 font-medium">Error: {error}</span>
          <button
            onClick={fetchPatients}
            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600">Loading patients...</span>
          </div>
        </div>
      )}

      {/* Patients List */}
      {!loading && allPatients.length > 0 ? (
        <div className="space-y-4">
          {allPatients.map((user) => (
            <div
              key={user._id}
              className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow"
            >
              {/* Card Header */}
              <div className="p-6 border-b">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  {/* Basic Info */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Patient</div>
                      <div className="flex items-center gap-2">
                        <div
                          // to={`/dashboard/patient/${user._id}`}
                          onClick={() => {
                            setProfileId(user._id)
                            setIsSuperPatientProfileOpen(true)
                            console.log("user id", user._id)
                          }}
                          className="font-medium text-blue-600 hover:underline text-sm"
                        >
                          {renderField(user.userDetails?.name)}
                        </div>
                        <span
                          className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${user.status === "active" ? "bg-green-500" : "bg-red-500"
                            }`}
                          title={user.status === "active" ? "Active" : "Inactive"}
                        ></span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {user.userDetails?.gender || "N/A"} • {user.medicalDetails?.bloodGroup || "N/A"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 mb-1">Contact</div>
                      <div className="text-sm font-medium">
                        {renderField(user.userDetails?.contact?.mobile)}
                      </div>
                      <div className="text-xs text-gray-400 truncate max-w-[180px] md:max-w-none" title={user.userDetails?.contact?.email || user.email}>
                        {renderField(user.userDetails?.contact?.email || user.email)}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 mb-1">OPD Visits</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {user.opdForms?.length || 0} {user.opdForms?.length === 1 ? "visit" : "visits"}
                      </div>
                      {/* {user.opdForms?.length > 0 && (
      <div className="text-xs text-gray-400">
        Last: {user.opdForms[user.opdForms.length - 1]?.opdNumber?.split('-')[1] || "N/A"}
      </div>
    )} */}
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 mb-1">Appointments</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {user.appointmentDetails?.length || 0} scheduled
                      </div>
                      <div className="text-xs text-gray-400">
                        {user.appointmentDetails?.length > 0 ? "Active" : "No pending"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 mb-1">Member Since</div>
                      <div className="text-sm font-medium">
                        {formatDate(user.createdAt)}
                      </div>
                      <div className="text-xs text-gray-400">
                        {user.medicalDetails?.medicalHistory?.length || 0} conditions
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCreateOPD(user)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Create OPD
                    </button>
                    <button
                      onClick={() => openAppointmentModal(user)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Appointment
                    </button>
                    <button
                      onClick={() => toggleTile(user._id)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      {expandedTiles[user._id] ? "Less" : "More"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedTiles[user._id] && (
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Personal Details */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <User size={20} className="text-blue-600" />
                        Personal Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Gender:</span>{" "}
                          {renderField(user.userDetails?.gender)}
                        </div>
                        <div>
                          <span className="text-gray-500">DOB:</span>{" "}
                          {user.userDetails?.dateOfBirth
                            ? formatDate(user.userDetails.dateOfBirth)
                            : "Not provided"}
                        </div>
                        <div>
                          <span className="text-gray-500">Contact:</span>{" "}
                          {renderField(user.userDetails?.contact?.mobile)}
                        </div>
                        <div>
                          <span className="text-gray-500">Address:</span>{" "}
                          {renderField(user.userDetails?.contact?.address)}
                        </div>
                      </div>
                    </div>

                    {/* Medical Details */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Heart size={20} className="text-red-600" />
                        Medical Details
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Blood Group:</span>{" "}
                          {renderField(user.medicalDetails?.bloodGroup)}
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Medical History:
                          </span>{" "}
                          {renderField(user.medicalDetails?.medicalHistory)}
                        </div>
                        <div>
                          <span className="text-gray-500">Dental History:</span>{" "}
                          {renderField(user.medicalDetails?.pastDentalHistory)}
                        </div>
                      </div>
                    </div>

                    {/* Previous OPD */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <ClipboardList size={20} className="text-purple-600" />
                        Previous OPD ({user?.opdForms?.length || 0})
                      </h3>
                      <div className="space-y-2 ">
                        {user?.opdForms && user.opdForms.length > 0 ? (
                          user.opdForms.map((opd) => (
                            <Link
                              key={opd._id}
                              to={`/dashboard/checkup/${opd._id}`}
                              className="block p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                            >
                              <div className="font-medium text-blue-600 text-sm">
                                {opd.opdNumber}
                              </div>
                              <div className="text-xs text-gray-500">
                                {opd.checkupInfo} - {formatDate(opd.createdAt)}
                              </div>
                            </Link>

                          ))
                        ) : (
                          <div className="text-gray-500 text-sm italic">
                            No OPD records found
                          </div>
                        )}
                        {/* {user?.opdForms?.length > 3 && (
                          <div className="text-xs text-gray-500">
                            +{user.opdForms.length - 3} more records
                          </div>
                        )} */}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        !loading && (
          <div className="text-center py-12 bg-white rounded-lg border">
            <div className="text-gray-500">
              No patients found matching your search criteria.
            </div>
          </div>
        )
      )}

      {/* Pagination */}
      {totalPages > 1 && !loading && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
              {totalRecords} patients
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 text-gray-500 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum =
                  Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 text-sm rounded-lg ${currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 border hover:bg-gray-50"
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-500 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Modal */}
      {isAppointmentModalOpen && selectedUser && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-4">Create Appointment
              <AddCategoryItemModal
                isOpen={isOpen}
                selectedCategory={selectedCategory}
                onClose={() => setIsOpen(false)}
                onAdded=""
              />
            </h2>
            <form onSubmit={handleAppointmentSubmit} className="space-y-4">
              <div>
                <label className="block font-semibold mb-1">
                  Patient:{" "}
                  <span className="font-normal">
                    {selectedUser.userDetails?.name}
                  </span>
                </label>
              </div>
              <div>
                <label className="block font-semibold mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min={today.toISOString().split("T")[0]}
                  max={maxDate.toISOString().split("T")[0]}
                  required
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">
                  Time Block <span className="text-red-500">*</span>
                </label>
                <select
                  value={timeBlock}
                  onChange={(e) => setTimeBlock(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" disabled>
                    Select time block
                  </option>
                  {timeBlocks.map((block) => (
                    <option key={block} value={block}>
                      {block}
                    </option>
                  ))}
                </select>
              </div>
              <label className="block font-semibold mb-1">
                Reason For Appointment <span className="text-red-500">*</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory("reasonOptions");
                    setIsOpen(!isOpen)
                  }}

                  className="ml-2 text-xs text-blue-600 underline">
                  add
                </button>
              </label>
              <ReasonDropdown
                // title="Reason For Appointment"
                item={selectedUser}  // Change from 'item' to 'selectedUser'
                localReason={reason}
                onChange={(field, value) => setReason(value)}
                reasonOptions={reasonOptions}
              />

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                  onClick={closeAppointmentModal}
                  disabled={creatingAppointment}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 ${creatingAppointment ? 'opacity-80 cursor-not-allowed' : ''}`}
                  disabled={creatingAppointment}
                >
                  {creatingAppointment ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    "Create Appointment"
                  )}
                </button>
              </div>
            </form>
            <AddCategoryItemModal
              isOpen={isOpen}
              selectedCategory={selectedCategory}
              onClose={() => setIsOpen(false)}
              onAdded=""
            />
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
      <SuperPatientProfile
        open={isSuperPatientProfileOpen}
        onClose={() => setIsSuperPatientProfileOpen(false)}
        patientId={profileId}
      />
    </div>
  );
};

export default DoctorUsersList;
